import React, { useState, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useGithubData } from '../../hooks/useGithubData';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { isSlackConnected } from '../../services/slack';
import { isNotionConnected } from '../../services/notion';
import { Plug, CheckCircle2, Github, Zap, Key, Shield } from 'lucide-react';
import { ConnectorPluginsPanel } from './ConnectorPluginsPanel';
import { OAuthSetupWizard } from './OAuthSetupWizard';
import { isConnectorConfigured, OAuthPlatform } from '../../services/oauthConnectors';
import { UserConnectButton } from '../shared/UserConnectButton';
// ─── Brand SVG Logos ────────────────────────────────────────────────────
function JiraLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 256" fill="none" data-id="element-3968">
      <defs data-id="element-3969">
        <linearGradient id="jira-a" x1="98%" y1="0%" x2="58%" y2="44%" data-id="element-3970">
          <stop offset="18%" stopColor="#0052CC" data-id="element-3971" />
          <stop offset="100%" stopColor="#2684FF" data-id="element-3972" />
        </linearGradient>
        <linearGradient id="jira-b" x1="0%" y1="0%" x2="42%" y2="44%" data-id="element-3973">
          <stop offset="18%" stopColor="#0052CC" data-id="element-3974" />
          <stop offset="100%" stopColor="#2684FF" data-id="element-3975" />
        </linearGradient>
      </defs>
      <path d="M244.658 0H121.707a55.502 55.502 0 0 0 55.502 55.502h22.649V77.37c.02 30.625 24.841 55.447 55.466 55.502V10.666C255.324 4.777 250.55 0 244.658 0Z" fill="#2684FF" data-id="element-3976" />

      <path d="M183.822 61.262H60.872c.019 30.625 24.84 55.447 55.466 55.502h22.649v21.868c.02 30.597 24.798 55.408 55.396 55.502V71.928c0-5.891-4.776-10.666-10.561-10.666Z" fill="url(#jira-a)" data-id="element-3977" />

      <path d="M122.951 122.489H0c0 30.653 24.85 55.502 55.502 55.502h22.72v21.796c.02 30.625 24.824 55.447 55.449 55.502V133.155c0-5.891-4.774-10.666-10.72-10.666Z" fill="url(#jira-b)" data-id="element-3978" />
    </svg>;
}
function ConfluenceLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 246" fill="none" data-id="element-3979">
      <defs data-id="element-3980">
        <linearGradient id="conf-a" x1="99%" y1="63%" x2="33%" y2="37%" data-id="element-3981">
          <stop offset="0%" stopColor="#0052CC" data-id="element-3982" />
          <stop offset="92%" stopColor="#2380FB" data-id="element-3983" />
        </linearGradient>
        <linearGradient id="conf-b" x1="0.5%" y1="34%" x2="66%" y2="60%" data-id="element-3984">
          <stop offset="0%" stopColor="#0052CC" data-id="element-3985" />
          <stop offset="92%" stopColor="#2380FB" data-id="element-3986" />
        </linearGradient>
      </defs>
      <path d="M9.26 187.726c-3.725 6.027-7.89 13.166-10.47 17.771a7.18 7.18 0 0 0 2.776 9.852l56.553 33.514a7.18 7.18 0 0 0 9.753-2.478c2.282-3.726 5.263-8.691 8.55-14.13 23.142-38.31 46.433-33.636 88.835-14.42l57.583 26.066a7.2 7.2 0 0 0 9.555-3.578l28.326-62.177a7.18 7.18 0 0 0-3.578-9.406c-14.27-6.467-42.81-19.34-60.582-27.44-74.78-34.076-138.725-31.598-187.3 46.426Z" fill="url(#conf-a)" data-id="element-3987" />

      <path d="M246.11 58.384c3.726-6.027 7.89-13.166 10.47-17.771a7.18 7.18 0 0 0-2.775-9.852L197.252 .247a7.18 7.18 0 0 0-9.754 2.479c-2.281 3.725-5.262 8.69-8.55 14.13-23.141 38.31-46.432 33.636-88.834 14.42L32.532 5.208a7.2 7.2 0 0 0-9.555 3.578L-5.35 70.963a7.18 7.18 0 0 0 3.579 9.406c14.27 6.467 42.81 19.34 60.582 27.44 74.78 34.076 138.724 31.598 187.3-49.426Z" fill="url(#conf-b)" data-id="element-3988" />
    </svg>;
}
function GoogleCalendarLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 200 200" fill="none" data-id="element-3989">
      <path d="M152.637 23.488H47.363L23.488 47.363v105.274l23.875 23.875h105.274l23.875-23.875V47.363L152.637 23.488Z" fill="#fff" data-id="element-3990" />

      <path d="M68.05 129.675c-3.538-2.388-5.988-5.875-7.313-10.475l8.2-3.375c.75 2.888 2.063 5.138 3.95 6.738 1.875 1.6 4.138 2.4 6.762 2.4 2.688 0 5-.863 6.95-2.575 1.95-1.713 2.925-3.875 2.925-6.475 0-2.675-1.025-4.875-3.075-6.575-2.05-1.7-4.575-2.55-7.575-2.55h-4.7v-8.1h4.225c2.6 0 4.813-.763 6.625-2.3 1.813-1.537 2.725-3.563 2.725-6.075 0-2.288-.838-4.125-2.5-5.5-1.675-1.375-3.775-2.063-6.3-2.063-2.45 0-4.4.663-5.85 1.988-1.45 1.325-2.513 2.95-3.175 4.863l-8.1-3.375c1.075-3.1 3.013-5.8 5.8-8.1 2.788-2.3 6.35-3.45 10.7-3.45 3.213 0 6.1.663 8.663 1.975 2.563 1.325 4.563 3.15 6 5.475 1.438 2.325 2.15 4.938 2.15 7.825 0 2.95-.675 5.45-2.025 7.5-1.35 2.05-3.05 3.625-5.1 4.725v.5c2.6 1.15 4.688 2.95 6.275 5.375 1.588 2.425 2.375 5.25 2.375 8.475 0 3.225-.813 6.1-2.45 8.625-1.625 2.525-3.875 4.5-6.725 5.925-2.863 1.425-6.075 2.138-9.65 2.138-4.275 0-8.1-1.2-11.638-3.588ZM130.3 86.375l-9 6.5-4.5-6.825 15.775-11.375h6.2v57.5h-8.475V86.375Z" fill="#1A73E8" data-id="element-3991" />

      <path d="M152.637 176.512l23.875-23.875V47.363l-23.875 23.875v105.274Z" fill="#EA4335" data-id="element-3992" />

      <path d="M47.363 176.512l23.875 23.875h81.399l-23.875-23.875H47.363Z" fill="#34A853" data-id="element-3993" />

      <path d="M23.488 152.637l23.875 23.875V71.238l-23.875-23.875v105.274Z" fill="#4285F4" data-id="element-3994" />

      <path d="M152.637 23.488l23.875 23.875H71.238L47.363 23.488h105.274Z" fill="#FBBC04" data-id="element-3995" />

      <path d="M176.512 47.363H71.238l-23.875-23.875-23.875 23.875L47.363 71.238h105.274l23.875-23.875Z" fill="#188038" opacity=".2" data-id="element-3996" />
    </svg>;
}
function GoogleDriveLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 87.3 78" fill="none" data-id="element-3997">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35Z" fill="#0066DA" data-id="element-3998" />

      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5L43.65 25Z" fill="#00AC47" data-id="element-3999" />

      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.95 10.3 7.8 13.5Z" fill="#EA4335" data-id="element-4000" />

      <path d="M43.65 25 57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2L43.65 25Z" fill="#00832D" data-id="element-4001" />

      <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h36.85c1.6 0 3.15-.45 4.5-1.2L59.8 53Z" fill="#2684FC" data-id="element-4002" />

      <path d="M73.4 26.5 60.65 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.6 25l16.2 28h27.45c0-1.55-.4-3.1-1.2-4.5l-12.65-22Z" fill="#FFBA00" data-id="element-4003" />
    </svg>;
}
function GmailLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 193" fill="none" data-id="element-4004">
      <path d="M58.182 192.05V93.14L27.507 65.077 0 49.504v125.091c0 9.658 7.825 17.455 17.455 17.455h40.727Z" fill="#4285F4" data-id="element-4005" />

      <path d="M197.818 192.05h40.727c9.659 0 17.455-7.826 17.455-17.455V49.505l-31.156 17.837-27.026 25.798v98.91Z" fill="#34A853" data-id="element-4006" />

      <path d="m58.182 93.14-4.174-38.647 4.174-36.989L128 69.868l69.818-52.364 4.669 34.992-4.669 40.644L128 145.504 58.182 93.14Z" fill="#EA4335" data-id="element-4007" />

      <path d="M197.818 17.504V93.14L256 49.504V26.231c0-21.585-24.64-33.89-41.89-20.945l-16.292 12.218Z" fill="#FBBC04" data-id="element-4008" />

      <path d="M0 49.504l26.759 20.07L58.182 93.14V17.504L41.89 5.286C24.61-7.66 0 4.646 0 26.23v23.273Z" fill="#C5221F" data-id="element-4009" />
    </svg>;
}
function SlackLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 256" fill="none" data-id="element-4010">
      <path d="M53.841 161.32c0 14.832-11.987 26.82-26.82 26.82-14.834 0-26.821-11.988-26.821-26.82 0-14.834 11.987-26.821 26.82-26.821h26.821v26.82ZM67.262 161.32c0-14.834 11.987-26.821 26.82-26.821 14.834 0 26.821 11.987 26.821 26.82v67.052c0 14.833-11.987 26.82-26.82 26.82-14.834 0-26.821-11.987-26.821-26.82v-67.051Z" fill="#E01E5A" data-id="element-4011" />

      <path d="M94.082 53.841c-14.834 0-26.821-11.987-26.821-26.82C67.261 12.186 79.248.2 94.082.2c14.833 0 26.82 11.987 26.82 26.82v26.821H94.083ZM94.082 67.262c14.833 0 26.82 11.987 26.82 26.82 0 14.834-11.987 26.821-26.82 26.821H27.02C12.187 120.903.2 108.916.2 94.082c0-14.833 11.987-26.82 26.82-26.82h67.062Z" fill="#36C5F0" data-id="element-4012" />

      <path d="M201.519 94.082c0-14.833 11.987-26.82 26.82-26.82 14.834 0 26.821 11.987 26.821 26.82 0 14.834-11.987 26.821-26.82 26.821h-26.821V94.082ZM188.098 94.082c0 14.834-11.987 26.821-26.82 26.821-14.834 0-26.821-11.987-26.821-26.82V27.02C134.457 12.187 146.444.2 161.278.2c14.833 0 26.82 11.987 26.82 26.82v67.062Z" fill="#2EB67D" data-id="element-4013" />

      <path d="M161.278 201.519c14.833 0 26.82 11.987 26.82 26.82 0 14.834-11.987 26.821-26.82 26.821-14.834 0-26.821-11.987-26.821-26.82v-26.821h26.82ZM161.278 188.098c-14.834 0-26.821-11.987-26.821-26.82 0-14.834 11.987-26.821 26.82-26.821h67.052c14.833 0 26.82 11.987 26.82 26.82 0 14.834-11.987 26.821-26.82 26.821h-67.051Z" fill="#ECB22E" data-id="element-4014" />
    </svg>;
}
function NotionLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 100 100" fill="none" data-id="element-4015">
      <path d="M6.017 4.313l55.333-4.087c6.797-.583 8.543-.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277-1.553 6.807-6.99 7.193L24.467 99.967c-4.08.193-6.023-.39-8.16-3.113L3.3 79.94c-2.333-3.113-3.3-5.443-3.3-8.167V11.113c0-3.497 1.553-6.413 6.017-6.8Z" fill="#fff" data-id="element-4016" />

      <path fillRule="evenodd" clipRule="evenodd" d="M61.35.227 6.017 4.313C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723.967 5.053 3.3 8.167l12.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257-3.89c5.433-.387 6.99-2.917 6.99-7.193V20.64c0-2.21-.873-2.847-3.443-4.733L74.167 3.143C69.893.14 68.147-.357 61.35.227ZM25.05 19.203c-5.18.313-6.353.387-9.3-1.947L8.927 11.5c-.777-.78-.34-1.753 1.553-1.947l53.193-3.887c4.467-.387 6.793 1.167 8.54 2.527l8.933 6.417c.39.193 1.36 1.36.193 1.36l-55.053 3.04-.237.193ZM19.27 88.14V32.2c0-2.527.777-3.693 3.103-3.887l59.737-3.5c2.14-.193 3.107 1.167 3.107 3.693v55.547c0 2.527-.39 4.667-3.883 4.86l-57.21 3.307c-3.497.193-4.853-1.003-4.853-4.08ZM74.36 35.887c.39 1.75 0 3.5-1.75 3.7l-2.723.5v41.067c-2.333 1.167-4.467 1.943-6.217 1.943-2.913 0-3.65-.97-5.833-3.5L39.577 52.29v24.693l5.833 1.36s0 3.5-4.857 3.5l-13.39.777c-.39-.777 0-2.723 1.357-3.11l3.497-.97V43.467l-4.857-.39c-.39-1.75.583-4.277 3.3-4.473l14.357-.97 19.053 29.15V45.387l-4.857-.583c-.39-2.143 1.163-3.7 3.103-3.89l13.197-.777v-4.25Z" fill="#000" data-id="element-4017" />
    </svg>;
}
function LinearLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 100 100" fill="none" data-id="element-4018">
      <path d="M1.227 61.613a48.988 48.988 0 0 0 37.16 37.16L1.227 61.613Z" fill="#5E6AD2" data-id="element-4019" />

      <path d="M.5 50.001c0 4.661.654 9.17 1.876 13.432L52.434 13.375A49.19 49.19 0 0 0 50 12.5C22.662 12.5.5 27.662.5 50Z" fill="#5E6AD2" opacity=".8" data-id="element-4020" />

      <path d="M7.762 73.883 73.883 7.762a49.41 49.41 0 0 0-8.69-5.153L2.609 65.193a49.41 49.41 0 0 0 5.153 8.69Z" fill="#5E6AD2" opacity=".9" data-id="element-4021" />

      <path d="M15.287 82.713 82.713 15.287a49.5 49.5 0 0 0-6.83-6.83L8.457 75.883a49.5 49.5 0 0 0 6.83 6.83Z" fill="#5E6AD2" data-id="element-4022" />

      <path d="M26.117 92.238 92.238 26.117a49.41 49.41 0 0 0-5.153-8.69L17.427 85.085a49.41 49.41 0 0 0 8.69 5.153Z" fill="#5E6AD2" opacity=".9" data-id="element-4023" />

      <path d="M50 99.5c-4.661 0-9.17-.654-13.432-1.876l49.058-49.058A49.19 49.19 0 0 0 87.5 50c0-22.338-15.162-37.5-37.5-37.5" fill="#5E6AD2" opacity=".8" data-id="element-4024" />

      <path d="M61.613 98.773a48.988 48.988 0 0 0 37.16-37.16L61.613 98.773Z" fill="#5E6AD2" data-id="element-4025" />
    </svg>;
}
function GoogleSheetsLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 351" fill="none" data-id="element-4026">
      <path d="M0 330.315V20.41C0 9.145 9.145 0 20.41 0h152.817l82.498 82.498v247.817c0 11.265-9.145 20.41-20.41 20.41H20.41C9.145 350.725 0 341.58 0 330.315Z" fill="#23A566" data-id="element-4027" />

      <path d="M175.227 0l82.498 82.498h-62.088c-11.265 0-20.41-9.145-20.41-20.41V0Z" fill="#1C8F5A" opacity=".7" data-id="element-4028" />

      <rect x="51" y="140" width="154" height="130" rx="6" fill="#fff" opacity=".9" data-id="element-4029" />

      <line x1="128" y1="140" x2="128" y2="270" stroke="#23A566" strokeWidth="2" data-id="element-4030" />

      <line x1="51" y1="183" x2="205" y2="183" stroke="#23A566" strokeWidth="2" data-id="element-4031" />

      <line x1="51" y1="226" x2="205" y2="226" stroke="#23A566" strokeWidth="2" data-id="element-4032" />
    </svg>;
}
function GoogleDocsLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 351" fill="none" data-id="element-4033">
      <path d="M0 330.315V20.41C0 9.145 9.145 0 20.41 0h152.817l82.498 82.498v247.817c0 11.265-9.145 20.41-20.41 20.41H20.41C9.145 350.725 0 341.58 0 330.315Z" fill="#4285F4" data-id="element-4034" />

      <path d="M175.227 0l82.498 82.498h-62.088c-11.265 0-20.41-9.145-20.41-20.41V0Z" fill="#3367D6" opacity=".7" data-id="element-4035" />

      <rect x="64" y="150" width="128" height="10" rx="5" fill="#fff" opacity=".8" data-id="element-4036" />

      <rect x="64" y="180" width="128" height="10" rx="5" fill="#fff" opacity=".8" data-id="element-4037" />

      <rect x="64" y="210" width="96" height="10" rx="5" fill="#fff" opacity=".8" data-id="element-4038" />

      <rect x="64" y="240" width="112" height="10" rx="5" fill="#fff" opacity=".8" data-id="element-4039" />
    </svg>;
}
function GoogleSlidesLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 351" fill="none" data-id="element-4040">
      <path d="M0 330.315V20.41C0 9.145 9.145 0 20.41 0h152.817l82.498 82.498v247.817c0 11.265-9.145 20.41-20.41 20.41H20.41C9.145 350.725 0 341.58 0 330.315Z" fill="#FBBC04" data-id="element-4041" />

      <path d="M175.227 0l82.498 82.498h-62.088c-11.265 0-20.41-9.145-20.41-20.41V0Z" fill="#E5A500" opacity=".7" data-id="element-4042" />

      <rect x="51" y="130" width="154" height="110" rx="6" fill="#fff" opacity=".9" data-id="element-4043" />

      <rect x="64" y="143" width="128" height="84" rx="4" fill="#FBBC04" opacity=".3" data-id="element-4044" />
    </svg>;
}
function GoogleFormsLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 256 351" fill="none" data-id="element-4045">
      <path d="M0 330.315V20.41C0 9.145 9.145 0 20.41 0h152.817l82.498 82.498v247.817c0 11.265-9.145 20.41-20.41 20.41H20.41C9.145 350.725 0 341.58 0 330.315Z" fill="#7248B9" data-id="element-4046" />

      <path d="M175.227 0l82.498 82.498h-62.088c-11.265 0-20.41-9.145-20.41-20.41V0Z" fill="#5C3AA1" opacity=".7" data-id="element-4047" />

      <circle cx="88" cy="165" r="10" fill="#fff" opacity=".8" data-id="element-4048" />
      <rect x="112" y="158" width="80" height="14" rx="4" fill="#fff" opacity=".6" data-id="element-4049" />

      <circle cx="88" cy="205" r="10" fill="#fff" opacity=".8" data-id="element-4050" />
      <rect x="112" y="198" width="80" height="14" rx="4" fill="#fff" opacity=".6" data-id="element-4051" />

      <circle cx="88" cy="245" r="10" fill="#fff" opacity=".8" data-id="element-4052" />
      <rect x="112" y="238" width="80" height="14" rx="4" fill="#fff" opacity=".6" data-id="element-4053" />
    </svg>;
}
function GitHubLogo({
  size = 24
}: {
  size?: number;
}) {
  return <Github size={size} className="text-white" data-id="element-4054" />;
}
function TeamsLogo({
  size = 24
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" data-id="element-4055">
      <path d="M20.625 6.547h-3.516a2.953 2.953 0 1 0-2.39-4.688 3.75 3.75 0 0 0-5.438 0A2.953 2.953 0 1 0 6.89 6.547H3.375A1.125 1.125 0 0 0 2.25 7.672v3.703a1.125 1.125 0 0 0 1.125 1.125h.375v6.375A1.125 1.125 0 0 0 4.875 20h14.25a1.125 1.125 0 0 0 1.125-1.125V12.5h.375a1.125 1.125 0 0 0 1.125-1.125V7.672a1.125 1.125 0 0 0-1.125-1.125Z" fill="#6264A7" data-id="element-4056" />
      <path d="M12 2.25a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" fill="#7B83EB" data-id="element-4057" />
      <circle cx="17" cy="5" r="2" fill="#5059C9" data-id="element-4058" />
      <rect x="5.5" y="9" width="13" height="9" rx="1" fill="#7B83EB" data-id="element-4059" />
      <rect x="8" y="11" width="3" height="1.5" rx="0.5" fill="white" data-id="element-4060" />
      <rect x="8" y="13.5" width="5" height="1" rx="0.5" fill="white" opacity="0.7" data-id="element-4061" />
    </svg>;
}
// ─── Animation Variants ─────────────────────────────────────────────────
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0
  }
};
// ─── Types ──────────────────────────────────────────────────────────────
interface ConnectorsPanelProps {
  onNavigate: (tab: string) => void;
}
// Map connector IDs to OAuth platform IDs (where applicable)
const CONNECTOR_OAUTH_MAP: Record<string, OAuthPlatform> = {
  jira: 'jira',
  confluence: 'confluence',
  github: 'github',
  slack: 'slack',
  notion: 'notion',
  teams: 'teams',
  gcalendar: 'google',
  gdrive: 'google',
  gmail: 'google',
  gsheets: 'google',
  gdocs: 'google',
  gslides: 'google',
  gforms: 'google'
};
// ─── Component ──────────────────────────────────────────────────────────
export function ConnectorsPanel({
  onNavigate
}: ConnectorsPanelProps) {
  const {
    enabled: githubEnabled
  } = useGithubData();
  const {
    isConnected: googleConnected,
    signIn: googleSignIn,
    error: googleError
  } = useGoogleAuth();
  const slackConnected = isSlackConnected();
  const [pluginsPanelOpen, setPluginsPanelOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<string | null>(null);
  const [oauthSetupOpen, setOauthSetupOpen] = useState(false);
  const openPlugins = (id: string) => {
    setSelectedConnector(id);
    setPluginsPanelOpen(true);
  };
  const connectors = [{
    id: 'jira',
    name: 'Jira',
    description: 'Sincronizá sprints, issues y backlog desde tu proyecto Jira',
    logo: JiraLogo,
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    status: 'connected' as const,
    actionLabel: 'Configurar',
    onAction: () => onNavigate('settings')
  }, {
    id: 'confluence',
    name: 'Confluence',
    description: 'Accedé a documentación y páginas del equipo',
    logo: ConfluenceLogo,
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/20',
    status: 'connected' as const,
    actionLabel: 'Ir al panel',
    onAction: () => onNavigate('docs')
  }, {
    id: 'github',
    name: 'GitHub',
    description: 'Repositorios, PRs, commits y CI/CD de tu organización',
    logo: GitHubLogo,
    bgColor: 'bg-slate-100/10',
    borderColor: 'border-slate-100/20',
    status: (githubEnabled ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: githubEnabled ? 'Ir al panel' : 'Configurar',
    onAction: () => onNavigate(githubEnabled ? 'github' : 'settings')
  }, {
    id: 'slack',
    name: 'Slack',
    description: 'Mensajes y notificaciones de canales',
    logo: SlackLogo,
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/20',
    status: (slackConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: slackConnected ? 'Ir al panel' : 'Configurar',
    onAction: () => onNavigate(slackConnected ? 'slack' : 'settings')
  }, {
    id: 'gcalendar',
    name: 'Google Calendar',
    description: 'Reuniones, eventos y disponibilidad del equipo',
    logo: GoogleCalendarLogo,
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gcalendar')
  }, {
    id: 'gdrive',
    name: 'Google Drive',
    description: 'Documentos, specs y archivos compartidos',
    logo: GoogleDriveLogo,
    bgColor: 'bg-emerald-400/10',
    borderColor: 'border-emerald-400/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gdrive')
  }, {
    id: 'gmail',
    name: 'Gmail',
    description: 'Correos y comunicaciones del equipo',
    logo: GmailLogo,
    bgColor: 'bg-red-400/10',
    borderColor: 'border-red-400/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gmail')
  }, {
    id: 'gsheets',
    name: 'Google Sheets',
    description: 'Hojas de cálculo, reportes y datos compartidos',
    logo: GoogleSheetsLogo,
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gsheets')
  }, {
    id: 'gdocs',
    name: 'Google Docs',
    description: 'Documentos de texto, propuestas y specs del equipo',
    logo: GoogleDocsLogo,
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gdocs')
  }, {
    id: 'gslides',
    name: 'Google Slides',
    description: 'Presentaciones, decks y material visual del equipo',
    logo: GoogleSlidesLogo,
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gslides')
  }, {
    id: 'gforms',
    name: 'Google Forms',
    description: 'Formularios, encuestas y recolección de datos',
    logo: GoogleFormsLogo,
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/20',
    status: (googleConnected ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: googleConnected ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('gforms')
  }, {
    id: 'notion',
    name: 'Notion',
    description: 'Bases de datos y documentos del equipo',
    logo: NotionLogo,
    bgColor: 'bg-slate-300/10',
    borderColor: 'border-slate-300/20',
    status: (isNotionConnected() ? 'connected' : 'disconnected') as 'connected' | 'disconnected',
    actionLabel: isNotionConnected() ? 'Ir al panel' : 'Conectar',
    onAction: () => onNavigate('notion')
  }, {
    id: 'teams',
    name: 'Microsoft Teams',
    description: 'Chats, canales y reuniones de tu organización',
    logo: TeamsLogo,
    bgColor: 'bg-violet-400/10',
    borderColor: 'border-violet-400/20',
    status: 'disconnected' as const,
    actionLabel: 'Conectar',
    onAction: () => {}
  }];
  const comingSoonConnectors = [{
    id: 'linear',
    name: 'Linear',
    description: 'Issues y proyectos de ingeniería',
    logo: LinearLogo,
    bgColor: 'bg-indigo-400/10',
    borderColor: 'border-indigo-400/20'
  }];
  const connectedCount = connectors.filter(c => c.status === 'connected').length;
  return <div className="h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative" data-id="element-4062">
      <div className="absolute top-0 left-1/4 w-1/2 h-64 bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4063" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4064">
        {/* Header */}
        <div className="relative pb-6 mb-8" data-id="element-4065">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4066">
            <div data-id="element-4067">
              <div className="flex items-center gap-3 mb-2" data-id="element-4068">
                <div className="relative w-10 h-10 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/10" data-id="element-4069">
                  <motion.div animate={{
                  scale: [1, 1.1, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: 'easeInOut'
                }} className="absolute inset-0 bg-indigo-500/20 rounded-xl blur-md" data-id="element-4070" />

                  <Plug size={22} className="text-indigo-400 relative z-10" data-id="element-4071" />
                </div>
                <div data-id="element-4072">
                  <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-400" data-id="element-4073">
                    Conectores
                  </h1>
                </div>
              </div>
              <p className="text-sm text-slate-400" data-id="element-4074">
                Administrá todas tus integraciones desde un solo lugar
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-4075">
              <button onClick={() => setOauthSetupOpen(true)} className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 hover:bg-slate-800 border border-slate-800 hover:border-indigo-500/30 rounded-xl text-sm font-medium text-slate-300 hover:text-indigo-300 transition-all" data-id="element-4076">
                <Key size={14} className="text-indigo-400" data-id="element-4077" />
                <span className="hidden sm:inline" data-id="element-4078">OAuth Setup</span>
                <span className="text-[9px] px-1.5 py-0.5 bg-indigo-950/50 text-indigo-400 border border-indigo-900/50 rounded-full font-bold uppercase tracking-wider" data-id="element-4079">
                  Admin
                </span>
              </button>
              <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-2.5" data-id="element-4080">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-id="element-4081" />
                <span className="text-sm font-medium text-slate-300" data-id="element-4082">
                  {connectedCount}
                </span>
                <span className="text-sm text-slate-500" data-id="element-4083">
                  / {connectors.length + comingSoonConnectors.length} activos
                </span>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" data-id="element-4084" />
        </div>

        {/* Google Auth Error Banner */}
        {googleError && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6 p-4 bg-amber-950/30 border border-amber-800/40 rounded-xl flex items-start gap-3" data-id="element-4085">
            <span className="text-amber-400 text-lg shrink-0" data-id="element-4086">⚠️</span>
            <div data-id="element-4087">
              <p className="text-sm font-medium text-amber-300 mb-1" data-id="element-4088">
                No se pudo conectar con Google
              </p>
              <p className="text-xs text-amber-400/70" data-id="element-4089">{googleError}</p>
            </div>
          </motion.div>}

        {/* Active Connectors Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-id="element-4090">
          {connectors.map(connector => {
          const oauthPlatform = CONNECTOR_OAUTH_MAP[connector.id];
          const hasOAuthConfig = oauthPlatform ? isConnectorConfigured(oauthPlatform) : false;
          return <motion.div variants={itemVariants} key={connector.id} data-id="element-4091">
                <Card className={`h-full flex flex-col p-5 bg-slate-900/40 backdrop-blur-sm border-slate-800/50 transition-all group ${connector.status === 'connected' ? 'hover:border-emerald-500/30' : 'hover:border-indigo-500/30'}`} data-id="element-4092">
                  <div className="flex items-start justify-between mb-4" data-id="element-4093">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${connector.bgColor} ${connector.borderColor}`} data-id="element-4094">
                      <connector.logo size={24} data-id="element-4095" />
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-4096">
                      {hasOAuthConfig && <span className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-950/40 border border-indigo-900/40 rounded text-[9px] text-indigo-400 font-medium" title="OAuth configurado" data-id="element-4097">
                          <Shield size={9} data-id="element-4098" />
                          OAuth
                        </span>}
                      {connector.status === 'connected' ? <Badge variant="success" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1" data-id="element-4099">
                          <CheckCircle2 size={12} data-id="element-4100" /> Conectado
                        </Badge> : <Badge variant="outline" className="text-slate-500 border-slate-700" data-id="element-4101">
                          Desconectado
                        </Badge>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-4102">
                    <h3 className="text-lg font-bold text-slate-200 mb-2 group-hover:text-indigo-300 transition-colors" data-id="element-4103">
                      {connector.name}
                    </h3>
                    <p className="text-sm text-slate-400 mb-6" data-id="element-4104">
                      {connector.description}
                    </p>
                  </div>
                  <div className="mt-auto pt-4 border-t border-slate-800/50" data-id="element-4105">
                    {connector.status === 'connected' ? <div className="flex gap-2" data-id="element-4106">
                        <Button variant="secondary" className="flex-1 justify-center" onClick={connector.onAction} data-id="element-4107">
                          {connector.actionLabel}
                        </Button>
                        <Button variant="outline" className="px-3 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/10" onClick={() => openPlugins(connector.id)} title="Plugins" data-id="element-4108">
                          <Zap size={16} data-id="element-4109" />
                        </Button>
                      </div> : oauthPlatform ? <UserConnectButton platform={oauthPlatform} onSuccess={() => {
                  // In a real app, this would trigger a re-fetch of connection status
                  // For now, we just reload the page to simulate state update
                  window.location.reload();
                }} data-id="element-4110" /> : <Button variant="primary" className="w-full justify-center" onClick={connector.onAction} data-id="element-4111">
                        {connector.actionLabel}
                      </Button>}
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Coming Soon Section */}
        <div className="mb-6" data-id="element-4112">
          <h2 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4113">
            Próximamente
          </h2>
          <p className="text-sm text-slate-400" data-id="element-4114">
            Nuevas integraciones en desarrollo
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60" data-id="element-4115">
          {comingSoonConnectors.map(connector => <motion.div variants={itemVariants} key={connector.id} data-id="element-4116">
              <Card className="h-full flex flex-col p-5 bg-slate-900/20 border-slate-800/30 border-dashed" data-id="element-4117">
                <div className="flex items-start justify-between mb-4" data-id="element-4118">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${connector.bgColor} ${connector.borderColor} grayscale`} data-id="element-4119">
                    <connector.logo size={24} data-id="element-4120" />
                  </div>
                  <Badge variant="outline" className="text-slate-500 border-slate-700 bg-slate-900" data-id="element-4121">
                    Próximamente
                  </Badge>
                </div>
                <div data-id="element-4122">
                  <h3 className="text-lg font-bold text-slate-300 mb-2" data-id="element-4123">
                    {connector.name}
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-4124">
                    {connector.description}
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>

      <ConnectorPluginsPanel connectorId={selectedConnector} open={pluginsPanelOpen} onClose={() => setPluginsPanelOpen(false)} data-id="element-4125" />

      <OAuthSetupWizard open={oauthSetupOpen} onClose={() => setOauthSetupOpen(false)} data-id="element-4126" />
    </div>;
}